import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ref, get, set } from '../services/phpDataAdapter';
import { database } from '../config/backend';
import { useAuth } from './AuthContext';

export type ThemeMode = 'light' | 'dark' | 'auto';
export type ColorScheme = 'light' | 'dark';

interface ThemeColors {
  primary: string;
  primaryDark: string;
  accent: string;
  background: string;
  surface: string;
  surfaceVariant: string;
  text: string;
  textMuted: string;
  border: string;
  sidebarBg: string;
  sidebarText: string;
  sidebarTextMuted: string;
  sidebarActiveBg: string;
  errorContainer: string;
  onErrorContainer: string;
}

interface Theme {
  mode: ThemeMode;
  colorScheme: ColorScheme;
  colors: ThemeColors;
  spacing: (factor: number) => string;
  radius: {
    sm: string;
    md: string;
    lg: string;
    pill: string;
  };
  shadow: {
    sm: string;
    md: string;
  };
}

interface ThemeContextType {
  theme: Theme;
  setThemeMode: (mode: ThemeMode) => Promise<void>;
  toggleTheme: () => Promise<void>;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Cores do tema claro
const lightColors: ThemeColors = {
  primary: '#2a9d8f',
  primaryDark: '#1f776c',
  accent: '#e76f51',
  background: '#f5f7fb',
  surface: '#ffffff',
  surfaceVariant: '#f3f4f6',
  text: '#1e293b',
  textMuted: '#475569',
  border: '#e2e8f0',
  sidebarBg: '#0f172a',
  sidebarText: '#e2e8f0',
  sidebarTextMuted: '#cbd5e1',
  sidebarActiveBg: '#1e293b',
  errorContainer: '#fee2e2',
  onErrorContainer: '#991b1b',
};

// Cores do tema escuro
const darkColors: ThemeColors = {
  primary: '#2a9d8f',
  primaryDark: '#1f776c',
  accent: '#e76f51',
  background: '#0f172a',
  surface: '#1e293b',
  surfaceVariant: '#334155',
  text: '#e2e8f0',
  textMuted: '#cbd5e1',
  border: '#334155',
  sidebarBg: '#020617',
  sidebarText: '#e2e8f0',
  sidebarTextMuted: '#cbd5e1',
  sidebarActiveBg: '#1e293b',
  errorContainer: '#7f1d1d',
  onErrorContainer: '#fca5a5',
};

// Detectar preferência do sistema
const getSystemColorScheme = (): ColorScheme => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [themeMode, setThemeModeState] = useState<ThemeMode>('auto');
  const [systemColorScheme, setSystemColorScheme] = useState<ColorScheme>(getSystemColorScheme());

  // Determinar esquema de cores atual
  const colorScheme: ColorScheme = themeMode === 'auto' ? systemColorScheme : themeMode;

  // Carregar preferência do usuário do servidor PHP/MySQL
  useEffect(() => {
    const loadUserTheme = async () => {
      if (!user?.uid) {
        // Se não há usuário, usar preferência do sistema
        setThemeModeState('auto');
        return;
      }

      try {
        const themeRef = ref(database, `users/${user.uid}/themeMode`);
        const snapshot = await get(themeRef);
        if (snapshot.exists()) {
          const savedMode = snapshot.val();
          if (['light', 'dark', 'auto'].includes(savedMode)) {
            setThemeModeState(savedMode as ThemeMode);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar tema do usuário:', error);
      }
    };

    loadUserTheme();
  }, [user?.uid]);

  // Escutar mudanças do sistema
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemColorScheme(e.matches ? 'dark' : 'light');
    };

    // Suporte para navegadores antigos
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  // Aplicar tema ao body/html
  useEffect(() => {
    const root = document.documentElement;
    const isDark = colorScheme === 'dark';
    
    if (isDark) {
      root.classList.add('dark');
      root.style.colorScheme = 'dark';
    } else {
      root.classList.remove('dark');
      root.style.colorScheme = 'light';
    }
  }, [colorScheme]);

  const setThemeMode = async (mode: ThemeMode) => {
    setThemeModeState(mode);
    
    // Salvar no servidor PHP/MySQL se houver usuário
    if (user?.uid) {
      try {
        await set(ref(database, `users/${user.uid}/themeMode`), mode);
      } catch (error) {
        console.error('Erro ao salvar tema no servidor PHP/MySQL:', error);
      }
    }
  };

  const toggleTheme = async () => {
    const newMode = colorScheme === 'light' ? 'dark' : 'light';
    await setThemeMode(newMode);
  };

  const theme: Theme = {
    mode: themeMode,
    colorScheme,
    colors: colorScheme === 'dark' ? darkColors : lightColors,
    spacing: (factor: number) => `${factor * 4}px`,
    radius: {
      sm: '6px',
      md: '10px',
      lg: '16px',
      pill: '999px',
    },
    shadow: {
      sm: '0 1px 2px rgba(15, 23, 42, 0.08)',
      md: '0 10px 25px rgba(15, 23, 42, 0.12)',
    },
  };

  const value: ThemeContextType = {
    theme,
    setThemeMode,
    toggleTheme,
    isDark: colorScheme === 'dark',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;

